import { Node } from '@tiptap/core'
import { VIDEO_SIZE } from '@/tiptap/constants/define'

import { getCssUnitWithDefault } from '@/tiptap/utils/utils'
import { VideoActionButton } from './components/ActionButton'
import VideoDialog from './components/video/VideoDialog.vue'

// Converts various video provider URLs to embeddable forms
function linkConvert(src) {
  if (!src) return src

  // Convert youtu.be links → embed
  src = src
    .replace('https://youtu.be/', 'https://www.youtube.com/watch?v=')
    .replace('watch?v=', 'embed/')

  // Convert youtube.com/shorts links → embed
  src = src.replace('https://youtube.com/shorts/', 'https://www.youtube.com/embed/')
  src = src.replace('https://www.youtube.com/shorts/', 'https://www.youtube.com/embed/')

  // Convert vimeo links → embed
  src = src.replace('https://vimeo.com/', 'https://player.vimeo.com/video/')

  // Convert bilibili links → embed
  const isBilibiliLink = /^https?:\/\/www\.bilibili\.com\/video\/.*/i.test(src)
  if (isBilibiliLink) {
    src = src
      .replace(/\?.*$/i, '')
      .replace('https://www.bilibili.com/video/', 'https://player.bilibili.com/player.html?bvid=')
  }

  // Convert Google Drive → preview
  if (src.includes('drive.google.com')) {
    src = src.replace('/view', '/preview')
  }

  // Remove any leftover query params like ?feature=share
  src = src.replace(/\?.*$/, '')

  // Force YouTube embeds to privacy-enhanced host
  // Normalize any youtube embed host to www.youtube-nocookie.com
  src = src.replace(
    /^https?:\/\/(?:www\.)?youtube\.com\/embed\//i,
    'https://www.youtube-nocookie.com/embed/'
  )
  src = src.replace(
    /^https?:\/\/youtube\.com\/embed\//i,
    'https://www.youtube-nocookie.com/embed/'
  )

  return src
}

const Video = /* @__PURE__ */ Node.create({
  name: 'video',

  group: 'block',

  atom: true,

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        renderHTML: ({ src }) => ({
          src: src ? linkConvert(src) : null,
        }),
      },
      width: {
        default: this.options.width,
        renderHTML: ({ width }) => ({
          width: getCssUnitWithDefault(width),
        }),
      },
      frameborder: {
        default: this.options.frameborder ? 1 : 0,
        parseHTML: () => (this.options.frameborder ? 1 : 0),
      },
      allowfullscreen: {
        default: this.options.allowFullscreen,
        parseHTML: () => this.options.allowFullscreen,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-video] iframe',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { width = '100%' } = HTMLAttributes ?? {}

    const iframeHTMLAttributes = {
      ...HTMLAttributes,
      width: '100%',
      height: '100%',
      // Explicitly allow features the YouTube player needs
      allow:
        'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share',
      // Keep referrers minimal to avoid cross-site noise and satisfy CSP
      referrerpolicy: 'strict-origin-when-cross-origin',
      // Hint browsers to defer offscreen iframes
      loading: 'lazy',
    }

    const responsiveStyle = `position: relative;overflow: hidden;display: flex;flex: 1;max-width: ${width};`
    const responsiveSizesStyle = `flex: 1;padding-bottom: ${(9 / 16) * 100}%;`

    const iframeDOM = ['iframe', iframeHTMLAttributes]
    const sizesDOM = ['div', { style: responsiveSizesStyle }]
    const responsiveDOM = ['div', { style: responsiveStyle }, sizesDOM, iframeDOM]

    const divAttrs = {
      ...this.options.HTMLAttributes,
      'data-video': '',
    }

    return ['div', divAttrs, responsiveDOM]
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },

      updateVideo:
        (options) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, options)
        },
    }
  },

  addOptions() {
    return {
      divider: false,
      spacer: false,
      allowFullscreen: true,
      frameborder: false,
      width: VIDEO_SIZE['size-medium'],
      HTMLAttributes: {
        class: 'iframe-wrapper',
        style: 'display: flex;justify-content: center;',
      },
      dialogComponent: () => VideoDialog,
      button: ({ editor, extension, t }) => {
        const { dialogComponent } = extension.options

        return {
          component: VideoActionButton,
          componentProps: {
            editor,
            t,
          },
          componentSlots: {
            dialog: dialogComponent(),
          },
        }
      },
    }
  },
})

export default Video
export { Video }